import {Expo, Power0, RoughEase, Sine, TimelineLite, TimelineMax, TweenLite, TweenMax} from "gsap";
import * as HLF from "html-living-framework";
import { AudioCategory } from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as SFX from "../Data/SoundEffect";

import { BalloonConfetti, ConfettiColourSuffixes } from "./BalloonConfetti";

export enum BalloonColourSuffixes {
    BLACK = "-black",
    GREEN_INDIA = "-green-india",
    GREEN_MEXICO = "-green-mexico",
    ORANGE = "-orange",
    RED = "-red",
    WHITE = "-white",
    YELLOW = "-yellow",
}

export class Balloon extends PIXI.Sprite {

    private readonly moveTimeline: gsap.Animation;
    private readonly wiggleTimeline: gsap.Animation;
    private readonly confettiColour: ConfettiColourSuffixes[];

    private constructor(
        pBalloonColour: BalloonColourSuffixes,
        pConfettiColour: ConfettiColourSuffixes | ConfettiColourSuffixes[],
    ) {
        super(HLF.PixiUtils.getTexture("balloon" + pBalloonColour, Constants.SPRITESHEET_GAME));
        if (!(pConfettiColour instanceof Array)) {
            pConfettiColour = [pConfettiColour];
        }
        this.confettiColour = pConfettiColour;

// interactivity
        this.interactive = true;
        this.cursor = "pointer";
        this.on("pointerdown", this.pop.bind(this));

        const rand = HLF.Random.floatBetween(-1, 1);
        const xDuration = HLF.Random.floatBetween(4, 6);
        const yDuration = HLF.Random.floatBetween(2, 6);
        const delayBeforePop = 2;

// float upwards with random starting x position
        this.moveTimeline = new TimelineLite()
            .fromTo(this, xDuration,
                {pixi: {positionX: 400 * rand}},
                {pixi: {positionX: 600 * rand}},
                0,
            )
            .fromTo(this, yDuration,
                {pixi: {positionY: 400}},
                {pixi: {positionY: -400}},
                0,
            )
            .call(this.pop.bind(this), [], undefined, xDuration + yDuration + delayBeforePop)
            .delay(HLF.Random.floatBetween(0, 0.5))
        ;
// wiggle left and right as well
        this.wiggleTimeline = TweenMax
            .fromTo(this, HLF.Random.floatBetween(1, 2),
                {pixi: {anchorX: HLF.Random.floatBetween(-0.5, -1)}, ease: Sine.easeInOut},
                {pixi: {anchorX: HLF.Random.floatBetween(0.5, 1)}, ease: Sine.easeInOut},
            )
            .yoyo(true)
            .repeat(-1)
            .delay(HLF.Random.floatBetween(0, 0.5))
        ;
    }
// static factories
    public static Black() {
        return new Balloon(BalloonColourSuffixes.BLACK, ConfettiColourSuffixes.BLACK);
    }
    public static Green_India() {
        return new Balloon(BalloonColourSuffixes.GREEN_INDIA, ConfettiColourSuffixes.GREEN);
    }
    public static Green_Mexico() {
        return new Balloon(BalloonColourSuffixes.GREEN_MEXICO, ConfettiColourSuffixes.GREEN);
    }
    public static Orange() {
        return new Balloon(BalloonColourSuffixes.ORANGE, ConfettiColourSuffixes.ORANGE);
    }
    public static Red() {
        return new Balloon(BalloonColourSuffixes.RED, ConfettiColourSuffixes.RED);
    }
    public static White() {
        return new Balloon(BalloonColourSuffixes.WHITE, ConfettiColourSuffixes.WHITE);
    }
    public static Yellow() {
        return new Balloon(BalloonColourSuffixes.YELLOW, ConfettiColourSuffixes.YELLOW);
    }
    public static India() {
        return [Balloon.White(), Balloon.Orange(), Balloon.Green_India()];
    }
    public static Mexico() {
        return [Balloon.White(), Balloon.Red(), Balloon.Green_Mexico()];
    }
    public static Egypt() {
        return [Balloon.Black(), Balloon.Red(), Balloon.Yellow()];
    }
// public functions
    public pop(): void {
// kill tweens (especially the pop callback at the end of moveTimeline)
        this.moveTimeline.kill();
        this.wiggleTimeline.kill();

        const confetti = new BalloonConfetti(this.confettiColour);
// center confetti on balloon position
        confetti.position.set(
            this.position.x + (this.width / 2 * -this.anchor.x),
            this.position.y + (this.height / 2 * -this.anchor.y),
        );
// add confetti to _parent_ (because we're about to be destroy'd)
        this.parent.addChild(confetti);
        confetti.play();
        const popSound = HLF.ArrayUtils.random([SFX.BALLOON_POP_1, SFX.BALLOON_POP_2]);
        Application.instance.audio.play(popSound, 1, false, AudioCategory.SFX);
        this.destroy();
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this.moveTimeline?.kill();
        this.wiggleTimeline?.kill();
        super.destroy(pOptions);
    }
}
